import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utilitarios'

// ---------------------------------------------------------------------
// Avatar
//
// Bolha com a inicial, usada onde não há foto de perfil: lista de
// participantes na administração e mural da comunidade.
// ---------------------------------------------------------------------

export function Avatar({
  nome,
  email,
  tamanho = 10,
}: {
  nome?: string | null | undefined
  email?: string | null | undefined
  tamanho?: 8 | 10 | undefined
}) {
  const inicial = (nome || email || '?').trim().charAt(0).toUpperCase()
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-coral/15 font-titulo font-bold text-coral',
        tamanho === 8 ? 'size-8 text-xs' : 'size-10 text-sm',
      )}
      aria-hidden
    >
      {inicial}
    </span>
  )
}

// ---------------------------------------------------------------------
// Botão
// ---------------------------------------------------------------------

const estilosBotao = cva(
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-botao)] font-titulo ' +
    'font-semibold transition-all active:scale-[0.97] disabled:pointer-events-none ' +
    'disabled:opacity-50 select-none',
  {
    variants: {
      variante: {
        principal: 'bg-coral text-white shadow-[var(--shadow-suave)] hover:bg-coral-escuro',
        secundario: 'bg-pink text-white shadow-[var(--shadow-suave)] hover:brightness-95',
        sucesso: 'bg-verde text-white shadow-[var(--shadow-suave)] hover:brightness-95',
        contorno: 'border-2 border-coral text-coral hover:bg-coral/10',
        fantasma: 'text-[var(--tinta-suave)] hover:bg-[var(--borda)]',
      },
      tamanho: {
        pequeno: 'h-11 px-4 text-sm',
        medio: 'h-13 px-6 text-base',
        grande: 'h-15 px-8 text-lg',
        bloco: 'h-14 w-full px-6 text-base',
      },
    },
    defaultVariants: { variante: 'principal', tamanho: 'medio' },
  },
)

export interface PropsBotao
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof estilosBotao> {
  carregando?: boolean
}

export function Botao({
  className,
  variante,
  tamanho,
  carregando,
  disabled,
  children,
  ...resto
}: PropsBotao) {
  return (
    <button
      type="button"
      className={cn(estilosBotao({ variante, tamanho }), className)}
      disabled={disabled || carregando}
      {...resto}
    >
      {carregando ? <Girador /> : children}
    </button>
  )
}

function Girador() {
  return (
    <span
      className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-label="Carregando"
    />
  )
}

// ---------------------------------------------------------------------
// Cartão
// ---------------------------------------------------------------------

export function Cartao({ className, ...resto }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('cartao p-5', className)} {...resto} />
}

export function TituloSecao({ children, acao }: { children: ReactNode; acao?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-titulo text-lg font-semibold">{children}</h2>
      {acao}
    </div>
  )
}

// ---------------------------------------------------------------------
// Barra de progresso
// ---------------------------------------------------------------------

export function BarraProgresso({
  valor,
  rotulo,
  className,
}: {
  valor: number
  rotulo?: string
  className?: string
}) {
  const percentual = Math.min(100, Math.max(0, valor))

  return (
    <div className={className}>
      <div
        className="h-3 w-full overflow-hidden rounded-full bg-[var(--borda)]"
        role="progressbar"
        aria-valuenow={percentual}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rotulo ?? 'Progresso'}
      >
        <div
          className="h-full rounded-full bg-linear-to-r from-coral to-dourado transition-[width] duration-700 ease-out"
          style={{ width: `${percentual}%` }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------
// Escolha entre opções (usado em quase todo o diário)
//
// Botões grandes em vez de <select>: com uma mão, no fim do dia, tocar
// é muito mais rápido do que abrir um seletor e rolar.
// ---------------------------------------------------------------------

export function GrupoEscolha<T extends string>({
  rotulo,
  opcoes,
  valor,
  aoEscolher,
  colunas = 2,
}: {
  rotulo: string
  opcoes: ReadonlyArray<{ valor: T; rotulo: string; emoji?: string }>
  valor: T | null
  aoEscolher: (valor: T) => void
  colunas?: 2 | 3 | 4
}) {
  const grade = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[colunas]

  return (
    <fieldset>
      <legend className="mb-2 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
        {rotulo}
      </legend>
      <div className={cn('grid gap-2', grade)}>
        {opcoes.map((opcao) => {
          const ativo = valor === opcao.valor
          return (
            <button
              key={opcao.valor}
              type="button"
              onClick={() => aoEscolher(opcao.valor)}
              aria-pressed={ativo}
              className={cn(
                'flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl border-2 px-2 py-2',
                'text-sm font-medium transition-all active:scale-[0.97]',
                ativo
                  ? 'border-coral bg-coral text-white shadow-[var(--shadow-suave)]'
                  : 'border-[var(--borda)] bg-[var(--cartao)] text-[var(--tinta-suave)]',
              )}
            >
              {opcao.emoji && <span className="text-xl leading-none">{opcao.emoji}</span>}
              <span>{opcao.rotulo}</span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

// ---------------------------------------------------------------------
// Escala 0–10 (energia, Roda da Vida)
// ---------------------------------------------------------------------

export function Escala({
  rotulo,
  valor,
  aoMudar,
  descricaoMin,
  descricaoMax,
}: {
  rotulo: string
  valor: number | null
  aoMudar: (valor: number) => void
  descricaoMin?: string
  descricaoMax?: string
}) {
  return (
    <fieldset>
      <legend className="mb-2 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
        {rotulo}
      </legend>
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, n) => {
          const ativo = valor === n
          return (
            <button
              key={n}
              type="button"
              onClick={() => aoMudar(n)}
              aria-pressed={ativo}
              aria-label={`${rotulo}: ${n}`}
              className={cn(
                'size-11 shrink-0 rounded-full border-2 font-titulo text-sm font-semibold transition-all active:scale-90',
                ativo
                  ? 'border-dourado bg-dourado text-marrom shadow-[var(--shadow-suave)]'
                  : 'border-[var(--borda)] bg-[var(--cartao)] text-[var(--tinta-apagada)]',
              )}
            >
              {n}
            </button>
          )
        })}
      </div>
      {(descricaoMin || descricaoMax) && (
        <div className="mt-1.5 flex justify-between text-xs text-[var(--tinta-apagada)]">
          <span>{descricaoMin}</span>
          <span>{descricaoMax}</span>
        </div>
      )}
    </fieldset>
  )
}

// ---------------------------------------------------------------------
// Item de checklist
// ---------------------------------------------------------------------

export function ItemCheck({
  titulo,
  descricao,
  marcado,
  aoAlternar,
  emoji,
}: {
  titulo: string
  descricao?: string | null
  marcado: boolean
  aoAlternar: () => void
  emoji?: string | undefined
}) {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      aria-pressed={marcado}
      className={cn(
        'flex w-full items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all active:scale-[0.99]',
        marcado
          ? 'border-verde bg-verde/10'
          : 'border-[var(--borda)] bg-[var(--cartao)] hover:border-coral/40',
      )}
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
          marcado ? 'border-verde bg-verde text-white' : 'border-[var(--borda)]',
        )}
        aria-hidden
      >
        {marcado && (
          <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor">
            <path
              d="M20 6 9 17l-5-5"
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-[15px] leading-snug font-medium',
            marcado && 'text-[var(--tinta-apagada)] line-through',
          )}
        >
          {emoji && <span className="mr-1.5">{emoji}</span>}
          {titulo}
        </span>
        {descricao && (
          <span className="mt-0.5 block text-xs text-[var(--tinta-apagada)]">{descricao}</span>
        )}
      </span>
    </button>
  )
}

// ---------------------------------------------------------------------
// Campo de texto
// ---------------------------------------------------------------------

export function Campo({
  rotulo,
  erro,
  dica,
  children,
}: {
  rotulo: string
  erro?: string | undefined
  dica?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
        {rotulo}
      </span>
      {children}
      {dica && !erro && <span className="mt-1 block text-xs text-[var(--tinta-apagada)]">{dica}</span>}
      {erro && (
        <span role="alert" className="mt-1 block text-xs font-medium text-pink">
          {erro}
        </span>
      )}
    </label>
  )
}

export const classesEntrada =
  'h-13 w-full rounded-2xl border-2 border-[var(--borda)] bg-[var(--cartao)] px-4 ' +
  'text-base text-[var(--tinta)] placeholder:text-[var(--tinta-apagada)] ' +
  'focus:border-coral focus:outline-none'

// ---------------------------------------------------------------------
// Estados vazios e de carregamento
// ---------------------------------------------------------------------

export function Vazio({
  titulo,
  descricao,
  emoji = '🌱',
  acao,
}: {
  titulo: string
  descricao?: string
  emoji?: string
  acao?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <span className="text-4xl" aria-hidden>
        {emoji}
      </span>
      <h3 className="font-titulo text-base font-semibold">{titulo}</h3>
      {descricao && (
        <p className="max-w-xs text-sm text-[var(--tinta-apagada)]">{descricao}</p>
      )}
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  )
}

export function Esqueleto({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-2xl bg-[var(--borda)]', className)}
      aria-hidden
    />
  )
}
